import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useLenis } from "lenis/react";
import { useLanguage } from "@/context/LanguageContext";
import { serviceDetails } from "@/servicesData";
import { waServiceUrl } from "@/i18n";
import { Reveal } from "@/components/Reveal";

const IMAGES = {
  "ship-chandler":
    "https://images.pexels.com/photos/16482922/pexels-photo-16482922/free-photo-of-ropes-on-vessel.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "working-gear-ppe":
    "https://images.pexels.com/photos/30169171/pexels-photo-30169171/free-photo-of-maritime-worker-in-orange-uniform-on-ship-deck.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "crew-documentation":
    "https://images.pexels.com/photos/37171408/pexels-photo-37171408/free-photo-of-crew-members-on-a-ship-deck-in-indonesia.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "visa-embassy":
    "https://images.pexels.com/photos/4922356/pexels-photo-4922356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const lenis = useLenis();
  const base = serviceDetails.id[slug];
  const data = (serviceDetails[lang] && serviceDetails[lang][slug]) || base;

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true, force: true });
    window.scrollTo(0, 0);
    if (data) {
      document.title = `${data.title} — CV Trinita Bahana Persada`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", data.lead);
    }
  }, [slug, lenis, data]);

  if (!base) return <Navigate to="/" replace />;

  return (
    <div data-testid="service-detail-page">
      <section className="relative overflow-hidden bg-navy pb-16 pt-32 sm:pt-40 lg:pb-20">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={IMAGES[slug]} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-navy/60" />
        </div>
        <span
          className="pointer-events-none absolute -top-8 right-0 select-none font-display text-[10rem] font-bold italic leading-none text-white/[0.05] sm:text-[16rem]"
          aria-hidden="true"
        >
          {data.number}
        </span>

        <div className="relative mx-auto max-w-7xl px-6 sm:px-10 lg:px-20">
          <Reveal>
            <Link
              to="/"
              data-testid="detail-back-link"
              className="group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-gold"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" aria-hidden="true" />
              {data.back}
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-10 flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.25em] text-gold sm:text-xs">
              <span className="font-display text-sm italic">{data.number}</span>
              <span className="h-px w-10 bg-gold" aria-hidden="true" />
              {data.subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1
              data-testid="detail-title"
              className="mt-6 max-w-3xl font-display text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              {data.title}
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p data-testid="detail-lead" className="mt-6 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-base">
              {data.lead}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-20">
          <div className="max-w-3xl space-y-5">
            {data.body.map((paragraph, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <p className="text-sm leading-relaxed text-ink/70 sm:text-base">{paragraph}</p>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <h2
              data-testid="detail-categories-title"
              className="mt-14 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl"
            >
              {data.categoriesTitle}
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.categories.map((category, i) => (
              <Reveal key={category.title} delay={0.04 * i}>
                <div
                  data-testid={`detail-category-${i}`}
                  className="h-full rounded-xl border border-navy/10 bg-mist/60 p-6 transition-colors duration-300 hover:border-gold/50"
                >
                  <h3 className="text-sm font-bold uppercase tracking-wider text-navy">
                    {category.title}
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {category.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-ink/70">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          {data.chips && (
            <>
              <Reveal>
                <h2
                  data-testid="detail-chips-title"
                  className="mt-14 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl"
                >
                  {data.chips.title}
                </h2>
              </Reveal>
              <Reveal delay={0.05}>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {data.chips.items.map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full border border-navy/15 bg-white px-4 py-2 text-xs font-semibold text-navy"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </Reveal>
            </>
          )}
        </div>
      </section>

      <section className="bg-mist py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-20">
          <Reveal>
            <h2
              data-testid="detail-extra-title"
              className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl"
            >
              {data.extraTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-ink/70 sm:text-base">
              {data.extraDesc}
            </p>
          </Reveal>

          {data.extraItems && (
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {data.extraItems.map((item, i) => (
                <Reveal key={item} delay={0.04 * i}>
                  <div className="flex items-start gap-3 rounded-lg bg-white p-4">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                    </span>
                    <p className="text-sm font-semibold leading-relaxed text-navy">{item}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          {data.flow && (
            <Reveal delay={0.1}>
              <div data-testid="detail-flow" className="mt-10 flex flex-wrap items-center gap-3">
                {data.flow.map((step, i) => (
                  <div key={step} className="flex items-center gap-3">
                    <span className="rounded-full bg-navy px-4 py-2 text-xs font-bold text-white">
                      {step}
                    </span>
                    {i < data.flow.length - 1 && (
                      <ArrowRight className="h-4 w-4 shrink-0 text-gold" aria-hidden="true" />
                    )}
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {data.note && (
            <Reveal delay={0.12}>
              <div
                data-testid="detail-note"
                className="mt-10 rounded-r-xl border-l-2 border-gold bg-white p-5 text-sm italic leading-relaxed text-ink/60"
              >
                {data.note}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-20">
          <Reveal>
            <h2
              data-testid="detail-targets-title"
              className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl"
            >
              {data.targetsTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {data.targets.map((target) => (
                <span
                  key={target}
                  className="rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy transition-colors duration-300 hover:border-gold hover:text-gold-dark"
                >
                  {target}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-20 lg:py-24">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-3xl px-6 text-center sm:px-10">
          <Reveal>
            <h2
              data-testid="detail-cta-title"
              className="font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl"
            >
              {data.ctaTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
              {data.ctaDesc}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              data-testid="detail-cta-button"
              href={waServiceUrl(data.title)}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-bold text-navy-950 shadow-[0_12px_40px_rgba(200,155,60,0.35)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-gold-light"
            >
              {data.ctaButton}
              <ArrowUpRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                aria-hidden="true"
              />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
